import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { BlockAccordion } from 'src/pages/Settings/memberships/components/landing-page-tab/block-accordion';
import { type LandingPageBlockType } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { getDragCursor } from 'src/pages/Settings/memberships/utils/get-drag-cursor';

interface SortableBlockItemProps {
  blockId: string;
  index: number;
  type: LandingPageBlockType;
  expanded: boolean;
  onExpandedChange: (blockId: string, expanded: boolean) => void;
  onRemove: (index: number, blockId: string) => void;
  onMove: (from: number, to: number) => void;
  moveUpToIndex: number | null;
  moveDownToIndex: number | null;
  readOnly: boolean;
}

export const SortableBlockItem = ({
  blockId,
  index,
  type,
  expanded,
  onExpandedChange,
  onRemove,
  onMove,
  moveUpToIndex,
  moveDownToIndex,
  readOnly,
}: SortableBlockItemProps): React.JSX.Element => {
  const canDrag = !readOnly;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    {
      id: blockId,
      disabled: !canDrag,
    },
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        backgroundColor: isDragging ? 'background.paper' : undefined,
        '& .block-drag-handle': { opacity: isDragging ? 1 : 0, transition: 'opacity 120ms' },
        '&:hover .block-drag-handle': { opacity: canDrag ? 1 : 0 },
      }}
    >
      <Box
        className="block-drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Reorder block"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'flex-start',
          // Spans the collapsed header so the icon centres against it.
          minHeight: (theme) => theme.spacing(6.5),
          py: 1.5,
          px: 0.25,
          cursor: getDragCursor(canDrag, isDragging),
        }}
      >
        <DynamicIcon
          name="drag_indicator"
          sx={{ color: 'text.secondary', fontSize: '1.125rem' }}
        />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <BlockAccordion
          index={index}
          blockId={blockId}
          type={type}
          expanded={expanded}
          onExpandedChange={onExpandedChange}
          onRemove={onRemove}
          onMove={onMove}
          moveUpToIndex={moveUpToIndex}
          moveDownToIndex={moveDownToIndex}
          readOnly={readOnly}
        />
      </Box>
    </Box>
  );
};
